
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp, Lock } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const contributors = [
  { id: "bryan", name: "Bryan Chen" },
  { id: "morgan", name: "Morgan Taylor" },
  { id: "pete", name: "Pete Gonzalez" },
  { id: "karen", name: "Karen Johnson" },
  { id: "giblet", name: "Giblet" },
];

const confirmers = [
  { id: "corey", name: "Corey Williams" },
  { id: "jessica", name: "Jessica Martinez" },
  { id: "miguel", name: "Miguel Santos" },
  { id: "giblet", name: "Giblet" },
];

const CallForDestructionBountyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [curator, setCurator] = useState<string>("");
  
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [contentLocations, setContentLocations] = useState<string>("");
  const [evidence, setEvidence] = useState<string>("");
  const [bountyAmount, setBountyAmount] = useState<string>("");
  const [selectedContributor, setSelectedContributor] = useState<string>("");
  const [selectedConfirmer, setSelectedConfirmer] = useState<string>("giblet");
  const [showPoweredByGiblet, setShowPoweredByGiblet] = useState<boolean>(true);

  // Updated default ownership values
  const [ownershipValues, setOwnershipValues] = useState({
    curator: 50,
    contributor: 45,
    confirmer: 5
  });

  const [isSectionExpanded, setIsSectionExpanded] = useState({
    area: false,
    bounty: false,
    curator: false
  });

  // Extract data from location state
  useEffect(() => {
    if (location.state) {
      setSelectedArea(location.state.selectedArea || "");
      setIsBounty(location.state.isBounty || null);
      setCurator(location.state.curator || "");
    }
  }, [location]);

  // Handle Giblet selection for Confirmer
  useEffect(() => {
    if (selectedConfirmer === "giblet") {
      setShowPoweredByGiblet(true);
      setOwnershipValues(prev => ({
        ...prev,
        confirmer: 5
      }));
    } else {
      setShowPoweredByGiblet(false);
      setOwnershipValues(prev => ({
        ...prev,
        confirmer: 10
      }));
    }
  }, [selectedConfirmer]);

  // Update contributor ownership when confirmer ownership changes
  useEffect(() => {
    if (isBounty === "yes") {
      const curatorValue = ownershipValues.curator;
      const confirmerValue = ownershipValues.confirmer;
      setOwnershipValues(prev => ({
        ...prev,
        contributor: 100 - curatorValue - confirmerValue
      }));
    }
  }, [ownershipValues.curator, ownershipValues.confirmer, isBounty]);

  const handleSubmit = () => {
    // In a real app, this would submit the form data to an API
    console.log("Form submitted:", {
      type: selectedArea,
      isBounty,
      curator,
      title,
      description,
      contentLocations,
      evidence,
      bountyAmount: isBounty === "yes" ? bountyAmount : "0",
      contributor: selectedContributor,
      confirmer: selectedConfirmer,
      ownershipSplit: ownershipValues
    });
    
    // Show success toast
    toast({
      title: "Destruction Request Sent Successfully",
      description: "Your Call for Destruction has been submitted and will appear in your mySPAI profile.",
    });
    
    // Navigate to demo creator rewards with the destruction request data
    navigate("/demo-creator-rewards", { 
      state: { 
        newCall: {
          title: title,
          area: selectedArea,
          type: isBounty === "yes" ? "Demo Destruction Bounty" : "Demo Call for Destruction",
          date: new Date().toLocaleDateString(),
          destructionDetails: {
            curator,
            description,
            contentLocations,
            evidence,
            bountyAmount: isBounty === "yes" ? bountyAmount : "0",
            contributor: selectedContributor,
            confirmer: selectedConfirmer,
            ownershipSplit: ownershipValues
          }
        }
      }
    });
  };

  const toggleSection = (section: string) => {
    setIsSectionExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      {/* Logo */}
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/curt-channel")}
        />
      </div>

      <h1 className="text-2xl font-bold mb-6">Destruction Request Details</h1>

      <div className="w-full max-w-3xl space-y-6">
        {/* Previous Selections Summary */}
        <div className="space-y-4">
          {/* Content Issue Type */}
          <Card className="border border-gray-200">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection("area")}
            >
              <div>
                <h2 className="text-lg font-semibold">Content Issue Type</h2>
                <p className="text-gray-600">{selectedArea}</p>
              </div>
              <div>
                {isSectionExpanded.area ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
          </Card>

          {/* Bounty Decision */}
          <Card className="border border-gray-200">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection("bounty")}
            >
              <div>
                <h2 className="text-lg font-semibold">Destruction Bounty</h2>
                <p className="text-gray-600">
                  {isBounty === "yes" ? "Yes, with token rewards" : "No, standard removal request"}
                </p>
              </div>
              <div>
                {isSectionExpanded.bounty ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
          </Card>

          {/* Curator */}
          <Card className="border border-gray-200">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection("curator")}
            >
              <div>
                <h2 className="text-lg font-semibold">Curator</h2>
                <p className="text-gray-600">{curator}</p>
              </div>
              <div>
                {isSectionExpanded.curator ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
          </Card>
        </div>

        {/* Destruction Request Form */}
        <Card className="border border-gray-200">
          <CardContent className="p-6 space-y-6">
            <div>
              <Label htmlFor="title" className="text-base font-medium">Title of Request</Label>
              <Input 
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a descriptive title for this destruction request"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-base font-medium">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe why this content should be removed"
                className="mt-1 min-h-24"
              />
            </div>

            <div>
              <Label htmlFor="location" className="text-base font-medium">Content Location</Label>
              <Textarea
                id="location"
                value={contentLocations}
                onChange={(e) => setContentLocations(e.target.value)}
                placeholder="List URLs, document IDs, or specific locations of the content to be removed"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="evidence" className="text-base font-medium">Supporting Evidence</Label>
              <Textarea
                id="evidence"
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="Provide evidence to support your destruction request (e.g., references to accurate information, IP documentation)"
                className="mt-1 min-h-24"
              />
            </div>

            {isBounty === "yes" && (
              <div>
                <Label htmlFor="bountyAmount" className="text-base font-medium">Bounty Amount (Tokens)</Label>
                <Input 
                  id="bountyAmount"
                  type="number"
                  value={bountyAmount}
                  onChange={(e) => setBountyAmount(e.target.value)}
                  placeholder="Enter amount of tokens to allocate"
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <h3 className="text-base font-medium mb-2">Select Contributor</h3>
              <Select onValueChange={setSelectedContributor} value={selectedContributor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contributor" />
                </SelectTrigger>
                <SelectContent>
                  {contributors.map(contributor => (
                    <SelectItem key={contributor.id} value={contributor.id}>
                      {contributor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <h3 className="text-base font-medium mb-2">Select Confirmer</h3>
              <Select onValueChange={setSelectedConfirmer} value={selectedConfirmer}>
                <SelectTrigger>
                  <SelectValue placeholder="Select confirmer" />
                </SelectTrigger>
                <SelectContent>
                  {confirmers.map(confirmer => (
                    <SelectItem key={confirmer.id} value={confirmer.id}>
                      {confirmer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {showPoweredByGiblet && (
                <div className="mt-2 p-2 bg-gray-50 border rounded-md flex items-center gap-2">
                  <Lock size={16} className="text-blue-500" />
                  <span className="text-sm font-medium">Powered by Giblet</span>
                </div>
              )}
            </div>

            {isBounty === "yes" && (
              <div>
                <h3 className="text-base font-medium mb-3">Ownership Distribution</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="border rounded-md p-3 text-center">
                    <p className="text-sm text-gray-600">Curator</p>
                    <p className="text-lg font-bold">{ownershipValues.curator}%</p>
                  </div>
                  <div className="border rounded-md p-3 text-center">
                    <p className="text-sm text-gray-600">Contributor</p>
                    <p className="text-lg font-bold">{ownershipValues.contributor}%</p>
                  </div>
                  <div className="border rounded-md p-3 text-center">
                    <p className="text-sm text-gray-600">Confirmer</p>
                    <p className="text-lg font-bold">{ownershipValues.confirmer}%</p>
                    {showPoweredByGiblet && (
                      <div className="flex justify-center mt-1">
                        <Lock size={14} className="text-blue-500" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <div className="flex items-start space-x-2">
          <Checkbox id="terms" />
          <div>
            <Label htmlFor="terms" className="cursor-pointer">
              I confirm that this destruction request is valid and necessary
            </Label>
            <p className="text-gray-500 text-sm mt-1">
              By submitting this form, you assert that the content identified should be removed for the reasons stated
              and that you have the authority to make this request.
            </p>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-between pt-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/call-for-destruction/curator")}
            className="border-gray-300"
          >
            Back
          </Button>
          
          <Button 
            onClick={() => navigate("/curt-channel")}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
          >
            Submit Destruction Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallForDestructionBountyDetails;
